import type { ReceiptSourceOption } from "@/lib/inboundReceiptsClient";
import type { OrgPickRow } from "./devPanelTypes";
import type { InboundReceiptsFilterState } from "./useInboundReceipts";

const selectClass =
  "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";
const inputClass =
  "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "processed", label: "processed" },
  { value: "skipped_duplicate", label: "skipped_duplicate" },
  { value: "error", label: "error" },
];

export function DevInboundReceiptsFilters(props: {
  organizationId: string;
  onOrganizationId: (id: string) => void;
  orgs: OrgPickRow[];
  orgsLoading: boolean;
  sources: ReceiptSourceOption[];
  filters: InboundReceiptsFilterState;
  patchFilters: (patch: Partial<InboundReceiptsFilterState>) => void;
  working: boolean;
  onRefresh: () => void;
  onReset: () => void;
}) {
  const { filters, patchFilters } = props;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-[12rem]">
        <label className="mb-1 block text-xs text-gray-500">Organization</label>
        <select
          className={`${selectClass} w-full`}
          value={props.organizationId}
          onChange={(e) => props.onOrganizationId(e.target.value)}
          disabled={props.orgsLoading || props.orgs.length === 0}
        >
          {props.orgs.length === 0 ? <option value="">No orgs</option> : null}
          {props.orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[10rem]">
        <label className="mb-1 block text-xs text-gray-500">Webhook / source</label>
        <select
          className={selectClass}
          value={filters.source}
          onChange={(e) => patchFilters({ source: e.target.value })}
        >
          <option value="">All sources</option>
          {props.sources.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Status</label>
        <select
          className={selectClass}
          value={filters.processingStatus}
          onChange={(e) => patchFilters({ processingStatus: e.target.value })}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value || "all"} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">From</label>
        <input
          className={inputClass}
          type="date"
          value={filters.sinceDate}
          onChange={(e) => patchFilters({ sinceDate: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">To</label>
        <input
          className={inputClass}
          type="date"
          value={filters.untilDate}
          onChange={(e) => patchFilters({ untilDate: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 pb-1.5 text-sm text-gray-800 dark:text-zinc-200">
        <input
          type="checkbox"
          checked={filters.unlinkedOnly}
          onChange={(e) => patchFilters({ unlinkedOnly: e.target.checked })}
        />
        Unlinked only
      </label>
      <button
        type="button"
        className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
        onClick={props.onRefresh}
        disabled={props.working || !props.organizationId}
      >
        {props.working ? "Loading…" : "Refresh"}
      </button>
      <button
        type="button"
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
        onClick={props.onReset}
      >
        Reset filters
      </button>
    </div>
  );
}
